import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import type { ResolverPlayer } from 'modules/manager/domain/placeholder';
import { REVIEW_CLIP_KIND_LABELS, REVIEW_CLIP_KINDS } from 'modules/match-video/domain/constants';
import { ManualGoalPairing, ReviewClip, ReviewClipKind, ReviewGoal, ReviewGoalMatch } from 'modules/match-video/domain/interfaces';
import React, { useMemo } from 'react';
import { UDSelect } from 'ud-form/components/select/component';
import { SelectOption } from 'ud-form/components/select/component/types';
import UDButton from 'ud-ui/components/button';
import UDIconButton from 'ud-ui/components/icon-button';
import UDSortableList from 'ud-ui/components/sortable-list';
import { UDEntityRow } from 'ud-ui/components/paged-list';
import UDText from 'ud-ui/components/text';

/** Select value of «this clip has no protocol goal»; a goal's value is its event id. */
const NO_GOAL = 'none';

interface ClipListProps {
  /** In review order: by camera time when added, then as moved by hand. */
  clips: readonly ReviewClip[];
  /** `null` — no match chosen yet: nothing to pair with. */
  goalMatch: ReviewGoalMatch | null;
  players?: ReadonlyMap<number, ResolverPlayer>;
  /** Team names by id, to tell two goals of one minute apart. */
  teams?: ReadonlyMap<number, string>;
  /** Goals chosen by hand; a clip left out keeps the pairing by order. */
  manual: ManualGoalPairing;
  /** `null` — the clip has no protocol goal. */
  onGoalChange: (clipId: string, eventId: number | null) => void;
  onKindChange: (clipId: string, kind: ReviewClipKind) => void;
  /** The clip goes to place `toIndex` — from the arrows and from dragging. */
  onMove: (clipId: string, toIndex: number) => void;
  onRemove: (clipId: string) => void;
  onClear: () => void;
  /** Files of the last add that were already in the list. */
  skipped: number;
  disabled?: boolean;
}

const idOfClip = (clip: ReviewClip) => clip.id;
const nameOfClip = (clip: ReviewClip) => clip.name;

const formatMinute = (goal: ReviewGoal) => (goal.extraMinutes > 0 ? `${goal.minutes}+${goal.extraMinutes}′` : `${goal.minutes}′`);

/**
 * The clips in the order they go into the review. Each row: the kind (read from the name, changeable), the protocol
 * goal of a goal clip, a grip to drag it, arrows to reorder without dragging (WCAG 2.5.7) and a bin. Goals pair by order by default — when the counts differ nothing
 * pairs (one missing clip would shift every scorer), and the goal is chosen right in the row. One goal may go to two
 * clips: a replay from another camera.
 */
const ClipList = (props: ClipListProps) => {
  const { clips, goalMatch, players, teams, manual, skipped, disabled } = props;
  const { onGoalChange, onKindChange, onMove, onRemove, onClear } = props;
  const { t } = useLingui();

  const kindOptions = useMemo(() => REVIEW_CLIP_KINDS.map((kind) => ({ label: t(REVIEW_CLIP_KIND_LABELS[kind]), value: kind })), [t]);

  const goalOptions = useMemo(() => {
    const describe = (goal: ReviewGoal) => {
      const scorer = goal.scorerId === undefined ? undefined : players?.get(goal.scorerId)?.name;
      const minute = formatMinute(goal);
      if (goal.isOwnGoal) return scorer ? t`Автогол: ${scorer}, ${minute}` : t`Автогол, ${minute}`;
      const team = teams?.get(goal.teamId);
      const label = scorer ? `${scorer}, ${minute}` : t`Гол на ${minute}`;
      return team ? `${label} · ${team}` : label;
    };
    return [
      { label: t`Без автора`, value: NO_GOAL },
      ...(goalMatch?.goals ?? []).map((goal) => ({ label: describe(goal), value: String(goal.eventId) })),
    ];
  }, [goalMatch?.goals, players, teams, t]);

  const selectedGoal = (clip: ReviewClip) => {
    const goal = goalMatch?.goalByClipId[clip.id];
    if (goal) return goalOptions.find((option) => option.value === String(goal.eventId)) ?? null;
    return manual[clip.id] === null ? goalOptions[0] : null;
  };

  const goalClips = clips.filter((clip) => clip.kind === 'goal');
  const unresolved = goalMatch?.mismatch ? goalClips.filter((clip) => !(clip.id in manual)).length : 0;
  const canChooseGoal = !!goalMatch && goalMatch.goals.length > 0;
  const goalClipCount = goalMatch?.goalClipCount ?? 0;
  const protocolGoalCount = goalMatch?.protocolGoalCount ?? 0;
  const skippedLabel = plural(skipped, { one: '# файл', few: '# файла', many: '# файлов', other: '# файла' });

  return (
    <div className="flex flex-col gap-3">
      {skipped > 0 && (
        <UDText type="footnote" role="status" className="text-inkSecondary" data-testid="match-video-clips-skipped">
          <Trans>Уже в списке, не добавлены повторно: {skippedLabel}.</Trans>
        </UDText>
      )}
      {unresolved > 0 && (
        <UDText type="footnote" role="status" className="text-CasePendingText" data-testid="match-video-goal-mismatch">
          <Trans>
            Клипов с голами: {goalClipCount}, голов в протоколе: {protocolGoalCount} — по порядку их не сопоставить. Выберите гол у
            каждого клипа, иначе плашка выйдет без автора.
          </Trans>
        </UDText>
      )}
      <UDSortableList
        items={clips}
        getId={idOfClip}
        itemLabel={nameOfClip}
        onMove={onMove}
        disabled={disabled}
        role="list"
        aria-label={t`Клипы обзора`}
        data-testid="match-video-clips"
        renderItem={(clip, { index, dragHandle }) => {
          const clipName = clip.name;
          return (
            <UDEntityRow
              leading={dragHandle}
              index={index + 1}
              title={clip.name}
              subtitle={clip.capturedAt ?? t`Время в имени не найдено`}
              actions={
                <div className="flex items-center gap-2">
                  {clip.kind === 'goal' && canChooseGoal && (
                    <UDSelect<false>
                      options={goalOptions}
                      value={selectedGoal(clip)}
                      onChange={(option) => {
                        const value = (option as SelectOption<string> | null)?.value;
                        if (value) onGoalChange(clip.id, value === NO_GOAL ? null : Number(value));
                      }}
                      placeholder={t`Выберите гол`}
                      isDisabled={disabled}
                      aria-label={t`Гол в клипе ${clipName}`}
                      divProps={
                        {
                          className: 'w-80 max-w-full',
                          'data-testid': `match-video-clip-goal-${index}`,
                        } as React.HTMLAttributes<HTMLDivElement>
                      }
                    />
                  )}
                  <UDSelect<false>
                    options={kindOptions}
                    value={kindOptions.find((option) => option.value === clip.kind) ?? null}
                    onChange={(option) => {
                      const value = (option as SelectOption<ReviewClipKind> | null)?.value;
                      if (value) onKindChange(clip.id, value);
                    }}
                    isDisabled={disabled}
                    aria-label={t`Тип клипа ${clipName}`}
                    divProps={
                      {
                        className: 'w-48 shrink-0',
                        'data-testid': `match-video-clip-kind-${index}`,
                      } as React.HTMLAttributes<HTMLDivElement>
                    }
                  />
                  <UDIconButton
                    icon={<ArrowUp />}
                    variant="ghost"
                    size="sm"
                    aria-label={t`Выше: ${clipName}`}
                    tooltip={t`Выше`}
                    disabled={disabled || index === 0}
                    onClick={() => onMove(clip.id, index - 1)}
                  />
                  <UDIconButton
                    icon={<ArrowDown />}
                    variant="ghost"
                    size="sm"
                    aria-label={t`Ниже: ${clipName}`}
                    tooltip={t`Ниже`}
                    disabled={disabled || index === clips.length - 1}
                    onClick={() => onMove(clip.id, index + 1)}
                  />
                  <UDIconButton
                    icon={<Trash2 />}
                    variant="danger"
                    size="sm"
                    aria-label={t`Убрать клип ${clipName}`}
                    tooltip={t`Убрать из обзора`}
                    disabled={disabled}
                    onClick={() => onRemove(clip.id)}
                  />
                </div>
              }
              data-testid={`match-video-clip-${index}`}
            />
          );
        }}
      />
      {clips.length > 1 && (
        <div>
          <UDButton variant="secondary" type="button" onClick={onClear} disabled={disabled}>
            <Trans>Убрать все клипы</Trans>
          </UDButton>
        </div>
      )}
    </div>
  );
};

export default ClipList;
